// US-BUCKET-12 -- Delete a bucket I created, with a transfer target
// for any balance.
//
// Flow:
//   - Zero balance: one-click confirm. The pending row carries
//     amount = 0 and target = null. Guardian still has to approve to
//     prevent accidental deletion (per AC #4).
//   - Positive balance: pick another active bucket I own to receive
//     the balance. The pending row carries amount = balance and
//     target = chosen bucket. Approval atomically moves the balance
//     AND deactivates the source.
//
// Ineligible buckets are filtered upstream (the Delete button on
// HomeScreen only renders when the bucket is not guardian-managed
// and not fixed-percent). The backend re-checks both eligibility
// and target validity on submit, so a stale UI can't slip an
// invalid request through.

import {
  type AppError,
  type BucketDeleteRequestResult,
  type BucketEntry,
  bucketDeleteRequest,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';

type Props = {
  sessionId: string;
  bucket: BucketEntry;
  childDisplayName: string;
  /** All active buckets for this child including the one being deleted. */
  siblings: BucketEntry[];
  currencyCode: string;
  onSubmitted: (result: BucketDeleteRequestResult) => void;
  onCancel: () => void;
};

export const DeleteBucketScreen: Component<Props> = (props) => {
  const [targetId, setTargetId] = createSignal<number | null>(null);
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const hasBalance = () => props.bucket.balanceMinorUnits > 0;

  // Eligible targets: any of MY buckets that aren't the one being
  // deleted. The bucketsList endpoint already returns only active
  // buckets so we don't need a per-row isActive filter. Guardian-
  // managed targets ARE allowed (the child owns them -- we're only
  // restricting which buckets the child can DELETE, not which can
  // receive a transfer). Backend re-checks ownership + active-ness.
  const candidates = createMemo(() =>
    props.siblings.filter((b) => b.bucketId !== props.bucket.bucketId),
  );

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    if (hasBalance() && targetId() === null) {
      setErrorMessage('Pick a bucket to receive the balance.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await bucketDeleteRequest({
        sessionId: props.sessionId,
        sourceBucketId: props.bucket.bucketId,
        // Only send the target when there's a balance to move; for
        // zero-balance deletions we send null (pure deactivation).
        targetBucketId: hasBalance() ? targetId() : null,
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      props.onSubmitted(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      class="action-screen--bucket-decision"
      eyebrow={`${props.childDisplayName}'s bucket`}
      title={`Delete ${props.bucket.bucketName}`}
      onBack={props.onCancel}
    >
      <p class="action-screen-intro">
        A guardian needs to approve this. Once they do, the bucket goes away -- and any balance
        moves to the bucket you pick below.
      </p>

      <form onSubmit={handleSubmit} novalidate data-testid="delete-bucket-form">
        <Show
          when={hasBalance()}
          fallback={
            <p class="pin-remove-status">
              This bucket has a zero balance. Submitting will ask a guardian to remove it.
            </p>
          }
        >
          <p class="pin-remove-status">
            This bucket holds {formatMinor(props.bucket.balanceMinorUnits, props.currencyCode)}.
            Pick another bucket to receive it.
          </p>

          <Show
            when={candidates().length > 0}
            fallback={
              <div class="form-error" role="alert">
                You don't have another active bucket to receive this balance. Add one first, then
                try again.
              </div>
            }
          >
            <fieldset class="field" style="border: none; padding: 0; margin: 0;">
              <legend class="field-label">Move balance to</legend>
              <For each={candidates()}>
                {(b) => (
                  <label style="display: flex; align-items: center; gap: 8px; padding: 6px 0;">
                    <input
                      type="radio"
                      name="target-bucket"
                      value={b.bucketId}
                      checked={targetId() === b.bucketId}
                      onChange={() => {
                        setTargetId(b.bucketId);
                        setErrorMessage(null);
                      }}
                      disabled={submitting()}
                      data-testid={`delete-target-${b.bucketId}`}
                    />
                    <span>
                      {b.bucketName} -- current balance{' '}
                      {formatMinor(b.balanceMinorUnits, props.currencyCode)}
                    </span>
                  </label>
                )}
              </For>
            </fieldset>
          </Show>
        </Show>

        <Show when={errorMessage()}>
          {(m) => (
            <div class="form-error" role="alert">
              {m()}
            </div>
          )}
        </Show>

        <div style="display: flex; gap: 12px; margin-top: 16px;">
          <button
            type="submit"
            class="primary pin-remove-primary"
            disabled={
              submitting() || (hasBalance() && (targetId() === null || candidates().length === 0))
            }
            data-testid="delete-bucket-submit"
          >
            {submitting() ? 'Submitting…' : 'Ask to delete'}
          </button>
          <button
            type="button"
            class="secondary"
            onClick={() => props.onCancel()}
            disabled={submitting()}
          >
            Cancel
          </button>
        </div>
      </form>
    </ActionScreen>
  );
};

function formatMinor(minorUnits: number, currencyCode: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currencyCode,
    }).format(minorUnits / 100);
  } catch {
    return `${(minorUnits / 100).toFixed(2)} ${currencyCode}`;
  }
}

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
    case 'not_found':
    case 'conflict':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
